import { IonIcon, IonSpinner } from '@ionic/react';
import { closeOutline, searchOutline } from 'ionicons/icons';
import React from 'react';
import CamelotKeyChip from './CamelotKeyChip';
import './SeedSearchField.scss';

export type SeedTrack = {
  id: string;
  title: string;
  artist: string;
  bpm: number;
  key: string;
  albumArt: string;
};

export const SeedSearchField: React.FC<{
  seedTrack: SeedTrack | null;
  query: string;
  isSearchMode: boolean;
  isSearching: boolean;
  searchResults: SeedTrack[];
  loading?: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onQueryChange: (value: string) => void;
  onSelectResult: (track: SeedTrack) => void;
  onClear: (event: React.MouseEvent) => void;
  onSeedClick?: () => void;
}> = ({
  seedTrack,
  query,
  isSearchMode,
  isSearching,
  searchResults,
  loading,
  inputRef,
  onQueryChange,
  onSelectResult,
  onClear,
  onSeedClick,
}) => {
  const showSearch = isSearchMode || !seedTrack;

  return (
    <div className="seed-search-field">
      {loading && !seedTrack && !isSearchMode ? (
        <div className="seed-search-loading">
          <IonSpinner name="crescent" />
        </div>
      ) : showSearch ? (
        <div className="seed-search-input-wrap">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') onQueryChange('');
            }}
            placeholder="Search for a seed track"
            className="seed-search-input"
          />
          {isSearching ? (
            <IonSpinner name="dots" className="seed-search-spinner" />
          ) : (
            <IonIcon icon={searchOutline} className="seed-search-icon" />
          )}
          {searchResults.length > 0 && (
            <div className="seed-search-results">
              {searchResults.map((track) => (
                <button
                  key={track.id}
                  type="button"
                  className="seed-search-result"
                  onClick={() => onSelectResult(track)}
                >
                  <img src={track.albumArt} alt="" />
                  <span className="seed-search-result-copy">
                    <span className="seed-search-result-title">
                      {track.title}
                    </span>
                    <span className="seed-search-result-artist">
                      {track.artist}
                    </span>
                  </span>
                  <span className="seed-search-result-meta">
                    {track.key && <CamelotKeyChip value={track.key} />}
                    {track.bpm > 0 && (
                      <span>
                        {track.bpm}
                        <span className="bpm-unit"> bpm</span>
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="seed-search-token-row">
          <div className="seed-search-token-card">
            <button
              type="button"
              className="seed-search-token"
              onClick={onSeedClick}
            >
              <img src={seedTrack.albumArt} alt="" />
              <span className="seed-search-token-copy">
                <span className="seed-search-token-title">
                  {seedTrack.title}
                </span>
                <span className="seed-search-token-artist">
                  {seedTrack.artist}
                </span>
              </span>
              <CamelotKeyChip value={seedTrack.key} />
              {seedTrack.bpm > 0 && (
                <span className="seed-search-token-bpm">
                  {seedTrack.bpm}
                  <span className="bpm-unit"> bpm</span>
                </span>
              )}
            </button>
            <button
              type="button"
              className="seed-search-remove"
              aria-label="Clear seed track"
              onClick={onClear}
            >
              <IonIcon icon={closeOutline} />
            </button>
          </div>
          <IonIcon icon={searchOutline} className="seed-search-icon" />
        </div>
      )}
    </div>
  );
};

export default SeedSearchField;
